import { useState } from "react";
import axios from "axios";
import { prefetchAppPagesOnLogin } from "@/lib/prefetch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, Mail } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LoginPage({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Inserisci email e password");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: email.trim().toLowerCase(),
        password
      }, { 
        withCredentials: true,
        timeout: 15000 // 15 secondi timeout
      });
      
      toast.success(`Benvenuto, ${response.data.name}!`);
      
      // ==================== PREFETCH PAGINE PRINCIPALI ====================
      // Fire-and-forget: scalda la cache backend + popola sessionStorage frontend
      // per Dashboard, Ordini, Payments, Interviews. Non blocca il redirect.
      prefetchAppPagesOnLogin(new Date().getFullYear());
      
      onLogin(response.data);
    } catch (e) {
      console.error("Login error:", e);
      if (e.code === 'ECONNABORTED' || e.message?.includes('timeout')) {
        toast.error("Connessione lenta. Riprova tra qualche secondo.");
      } else if (e.response?.status === 401) {
        toast.error("Credenziali non valide");
      } else if (e.response?.status === 500) {
        toast.error("Errore del server. Riprova tra qualche secondo.");
      } else if (!e.response) {
        toast.error("Errore di connessione. Verifica la tua connessione internet.");
      } else {
        toast.error("Errore durante il login. Riprova.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-24 h-24 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl overflow-hidden">
            <img
              src="/assets/logo-ingenio.jpg"
              alt="Showroom Ingenio"
              className="w-full h-full object-contain"
            />
          </div>
          <h1 className="text-3xl font-bold text-white">Showroom Ingenio</h1>
          <p className="text-blue-200 mt-2">Gestionale AI</p>
        </div>

        {/* Login Card */}
        <Card className="shadow-2xl">
          <CardHeader>
            <CardTitle className="text-center text-xl">Accedi al CRM</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="email@esempio.it"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    data-testid="login-email"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                    data-testid="login-password"
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? "Accesso in corso..." : "Accedi"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-slate-500 text-sm mt-6">
          © 2026 Showroom Ingenio SRL
        </p>
      </div>
    </div>
  );
}
